import { useMemo, useState } from "react";
import type { TemplateFormDef, TemplateFormFieldId } from "../constants/templateForms";
import type { PdfLocale } from "../utils/templateFormPdf";
import {
  applyPerFilingPrefill,
  createInitialValues,
  documentNameFor,
  nextDocumentNumber,
  type TemplateFormPrefill,
  type TemplateFormValue,
  type TemplateFormValues,
} from "../utils/templateForm";

export interface TemplateFormState {
  values: TemplateFormValues;
  setValue: (id: TemplateFormFieldId, value: TemplateFormValue) => void;
  /** Copies a previously filed form's answers in, keeping this form's own
   *  number and name — a pre-filled start, not a duplicate of that document. */
  loadValues: (values: TemplateFormValues) => void;
  /**
   * The number this form is predicted to get, for display and for the preview.
   *
   * Read-only, and deliberately so: the number is a database guarantee
   * (`next_template_form_number` under `UNIQUE (project_id, template_id,
   * document_number)`), reserved as the document is filed. Nothing typed here
   * could be honoured, so nothing is typed here.
   */
  documentNumber: string;
  documentName: string;
  /** True while the name is still the derived `<slug>-nr-<n>` — the save may
   *  then rebuild it from the number the database actually reserves, which the
   *  client-side seed only predicts. A typed name is kept verbatim. */
  nameFollowsNumber: boolean;
  changeName: (value: string) => void;
  attachmentsInPdf: boolean;
  setAttachmentsInPdf: (checked: boolean) => void;
  signDigitally: boolean;
  setSignDigitally: (checked: boolean) => void;
  /** The language the PDF prints in, independent of the interface. */
  pdfLocale: PdfLocale;
  setPdfLocale: (locale: PdfLocale) => void;
  /** True once the user has touched anything — closing then needs a confirm. */
  dirty: boolean;
  /** False while the form is not yet fileable — Save stays disabled. */
  canSave: boolean;
  /** Required fields still empty, in schema order. Marks them in the form. */
  missingFields: TemplateFormFieldId[];
}

/** A required field counts as filled when it holds something other than
 *  whitespace; a required group counts when it has at least one entry. */
function isFilled(value: TemplateFormValue | undefined): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === "string" && value.trim().length > 0;
}

function missingRequired(form: TemplateFormDef, values: TemplateFormValues): TemplateFormFieldId[] {
  return form.sections.flatMap((section) =>
    section.fields
      .filter((field) => field.required && !isFilled(values[field.id]))
      .map((field) => field.id)
  );
}

/**
 * Everything one open template form holds: its field values, the document
 * number and name, and the two output options. The name follows the number
 * (`kaetud-toode-akt-nr-3`) until the user types over it.
 */
export function useTemplateFormState(
  form: TemplateFormDef,
  prefill: TemplateFormPrefill,
  existingDocumentNames: string[],
  signByDefault: boolean
): TemplateFormState {
  // Seeded from both name slugs, each with its own locale's separator: a
  // project holding English-named acts (`…-no-5`) would otherwise under-count
  // and show a number already taken. The save still reserves the authoritative
  // one through next_template_form_number.
  const startNumber = useMemo(
    () =>
      Math.max(
        nextDocumentNumber(existingDocumentNames, form.nameSlug.et, "et"),
        nextDocumentNumber(existingDocumentNames, form.nameSlug.en, "en")
      ),
    [existingDocumentNames, form.nameSlug.et, form.nameSlug.en]
  );
  const [values, setValues] = useState<TemplateFormValues>(() =>
    createInitialValues(form, prefill)
  );
  // `null` means "still following the document number". Deriving rather than
  // seeding state keeps a refresh while the form is open (an upload finishing,
  // a list refetch) from leaving a stale number behind that collides on save.
  const [typedName, setTypedName] = useState<string | null>(null);
  const [attachmentsInPdf, setAttachmentsInPdf] = useState(true);
  const [signDigitally, setSignDigitally] = useState(signByDefault);
  // Estonian by default whatever the interface language: these are Estonian
  // statutory documents, and the Estonian rendering is the one that gets filed.
  const [pdfLocale, setPdfLocale] = useState<PdfLocale>("et");
  const [dirty, setDirty] = useState(false);

  const documentNumber = String(startNumber);
  // An emptied name stays empty — `canSave` blocks saving a nameless form.
  // Follows the PDF's language until typed over, so switching the document to
  // English renames the file with it.
  const documentName =
    typedName ?? documentNameFor(form.nameSlug[pdfLocale], startNumber, pdfLocale);

  const changeName = (value: string) => {
    setDirty(true);
    setTypedName(value);
  };

  const missing = missingRequired(form, values);

  return {
    values,
    setValue: (id, value) => {
      setDirty(true);
      setValues((previous) => ({ ...previous, [id]: value }));
    },
    loadValues: (loaded) => {
      setDirty(true);
      // Layered over the schema's initial values, so a field the source form
      // did not carry (a newer field, a different template) still exists rather
      // than going undefined. Then the per-filing prefill goes back on top:
      // `loaded` must not carry the source document's date and author into
      // this one — see `applyPerFilingPrefill`.
      setValues(
        applyPerFilingPrefill(form, prefill, { ...createInitialValues(form, prefill), ...loaded })
      );
    },
    documentNumber,
    documentName,
    nameFollowsNumber: typedName === null,
    changeName,
    attachmentsInPdf,
    setAttachmentsInPdf: (checked) => {
      setDirty(true);
      setAttachmentsInPdf(checked);
    },
    signDigitally,
    setSignDigitally: (checked) => {
      setDirty(true);
      setSignDigitally(checked);
    },
    pdfLocale,
    setPdfLocale: (locale) => {
      setDirty(true);
      setPdfLocale(locale);
    },
    dirty,
    canSave: documentName.trim().length > 0 && missing.length === 0,
    missingFields: missing,
  };
}
